import React, { useMemo } from 'react'
import { Alert, Box, Divider, Grid, Typography, Chip } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ReplyIcon from '@mui/icons-material/Reply'
import CallIcon from '@mui/icons-material/Call'
import ArticleIcon from '@mui/icons-material/Article'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'

const BUTTON_ICON_MAP = {
  URL: OpenInNewIcon,
  COPY_CODE: ContentCopyIcon,
  QUICK_REPLY: ReplyIcon,
  PHONE_NUMBER: CallIcon,
  FLOW: ArticleIcon,
}

const convertWhatsappToHTML = (text, variables = []) => {
  if (!text) return ''

  return text
    .replace(/{{(\d+)}}/g, (_, i) => variables?.[i - 1] || '')
    .replace(/\*(.*?)\*/g, '<b>$1</b>')
    .replace(/_(.*?)_/g, '<i>$1</i>')
    .replace(/~(.*?)~/g, '<s>$1</s>')
    .replace(/\n/g, '<br>')
}

export const MessageTemplatePreview = ({ rows = [], selectedTemplate, headerImagePreviewUrl }) => {
  const firstRow = useMemo(() => rows?.[0] || null, [rows])

  const renderedTemplate = useMemo(
    () => convertWhatsappToHTML(selectedTemplate?.bodyText || '', firstRow?.variables || []),
    [selectedTemplate?.bodyText, firstRow?.variables],
  )

  const buttonsWithValue = useMemo(() => {
    let dynamicIndex = 0

    return (selectedTemplate?.buttons || []).map((button) => {
      const value = button.isDynamic ? firstRow?.buttonVariables?.[dynamicIndex] : undefined

      if (button.isDynamic) dynamicIndex += 1

      return { ...button, value }
    })
  }, [selectedTemplate?.buttons, firstRow?.buttonVariables])

  if (!firstRow)
    return (
      <Grid item xs={12}>
        <Alert severity='info'>No preview data available</Alert>
      </Grid>
    )

  return (
    <Grid item xs={12}>
      <Box
        sx={{
          backgroundColor: '#ece5dd',
          borderRadius: 3,
          maxWidth: 400,
          border: '1px solid #ccc',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', top: 8, right: 12, zIndex: 2 }}>
          <Chip label='Preview Only' size='small' color='warning' variant='outlined' />
        </Box>
        <Box sx={{ backgroundColor: '#075E54', color: 'white', px: 2, py: 1 }}>
          <Typography variant='subtitle2' sx={{ opacity: 0.8 }}>
            To:
          </Typography>
          <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
            {firstRow?.phoneNumber || '-'}
          </Typography>
          <Typography variant='caption' sx={{ opacity: 0.7 }}>
            Preview recipient
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              backgroundColor: '#fff',
              borderRadius: 2,
              boxShadow: '0 1px 1px rgba(0,0,0,0.1)',
              maxWidth: '85%',
              overflow: 'hidden',
            }}
          >
            {!!headerImagePreviewUrl && selectedTemplate?.hasImageHeader && (
              <Box
                component='img'
                src={headerImagePreviewUrl}
                alt='Header preview'
                sx={{
                  width: '100%',
                  display: 'block',
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                }}
              />
            )}
            <Box sx={{ p: 1.5 }}>
              <Typography
                variant='body2'
                component='div'
                dangerouslySetInnerHTML={{ __html: renderedTemplate }}
              />
              <Typography
                variant='caption'
                sx={{ display: 'block', textAlign: 'right', mt: 0.5, opacity: 0.5 }}
              >
                Preview • Not sent
              </Typography>
            </Box>
            {buttonsWithValue.length > 0 && (
              <Box>
                {buttonsWithValue.map((button, index) => {
                  const Icon = BUTTON_ICON_MAP[button.type] || ChatBubbleOutlineIcon

                  return (
                    <Box key={index}>
                      <Divider />
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                          py: 1,
                          color: '#00a5f4',
                        }}
                      >
                        <Icon fontSize='small' />
                        <Typography variant='body2'>{button.text}</Typography>
                      </Box>
                      {button.isDynamic && (
                        <Typography
                          variant='caption'
                          sx={{ display: 'block', textAlign: 'center', pb: 1, opacity: 0.6 }}
                        >
                          {button.value
                            ? `→ ${
                                button.type === 'URL' && button.urlPattern
                                  ? button.urlPattern.replace(/{{\d+}}/, button.value)
                                  : button.value
                              }`
                            : '(missing button value)'}
                        </Typography>
                      )}
                    </Box>
                  )
                })}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Typography variant='caption' color='text.secondary' sx={{ mt: 1, display: 'block' }}>
        This is only a preview. The actual message has not been sent.
      </Typography>
    </Grid>
  )
}
